import { CONFIGURATOR_STEPS } from "../content/configurator";
import {
  getAttributionParams,
  getInternalReferrer,
  initAttributionSession,
  recordSessionPage,
} from "./analyticsAttribution";
import { getProjectByPortfolioPath } from "./portfolioRoutes";

export const GA_MEASUREMENT_ID = "G-WZT9QGH7N1";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type PageKind =
  | "home"
  | "contact"
  | "start_project"
  | "portfolio"
  | "portfolio_category"
  | "project"
  | "services"
  | "offering"
  | "about"
  | "faq"
  | "experience"
  | "search"
  | "testimonials"
  | "other";

export type PageViewMeta = {
  page_kind: PageKind;
  page_title?: string;
  project_id?: string;
  project_title?: string;
  project_category?: string;
  portfolio_category?: string;
  offering_kind?: string;
  offering_id?: string;
};

function gtagEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params);
}

function baseParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const internalReferrer = getInternalReferrer();
  return {
    ...getAttributionParams(),
    ...(internalReferrer ? { internal_referrer: internalReferrer } : {}),
    ...extra,
  };
}

export function classifyPage(pathname: string): PageViewMeta {
  if (pathname === "/") return { page_kind: "home" };
  if (pathname === "/contact") return { page_kind: "contact" };
  if (pathname === "/start-project") return { page_kind: "start_project" };
  if (pathname === "/ux-portfolio") return { page_kind: "portfolio" };
  if (pathname === "/about") return { page_kind: "about" };
  if (pathname === "/services") return { page_kind: "services" };
  if (pathname === "/experience") return { page_kind: "experience" };
  if (pathname === "/search") return { page_kind: "search" };
  if (pathname === "/testimonials") return { page_kind: "testimonials" };
  if (pathname.startsWith("/faq")) return { page_kind: "faq" };

  const offeringProduct = pathname.match(/^\/services\/products\/([^/]+)$/);
  if (offeringProduct) {
    return {
      page_kind: "offering",
      offering_kind: "product",
      offering_id: decodeURIComponent(offeringProduct[1]),
    };
  }

  const offeringCapability = pathname.match(/^\/services\/capabilities\/([^/]+)$/);
  if (offeringCapability) {
    return {
      page_kind: "offering",
      offering_kind: "capability",
      offering_id: decodeURIComponent(offeringCapability[1]),
    };
  }

  const projectMatch = pathname.match(/^\/ux-portfolio\/([^/]+)\/([^/]+)$/);
  if (projectMatch) {
    const category = decodeURIComponent(projectMatch[1]);
    const projectSlug = decodeURIComponent(projectMatch[2]);
    const project = getProjectByPortfolioPath(category, projectSlug);
    return {
      page_kind: "project",
      project_id: project?.id ?? projectSlug,
      project_title: project?.title?.replace(/<[^>]+>/g, "") ?? projectSlug,
      project_category: category,
    };
  }

  const categoryMatch = pathname.match(/^\/ux-portfolio\/([^/]+)$/);
  if (categoryMatch) {
    return {
      page_kind: "portfolio_category",
      portfolio_category: decodeURIComponent(categoryMatch[1]),
    };
  }

  return { page_kind: "other" };
}

/** Send a GA4 page view for client-side route changes (React Router history). */
export function trackPageView(pathWithSearch: string): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  initAttributionSession(pathWithSearch);
  recordSessionPage(pathWithSearch);

  const [pathname] = pathWithSearch.split("?");
  const meta = classifyPage(pathname);
  const params = baseParams(meta);

  window.gtag("config", GA_MEASUREMENT_ID, {
    page_path: pathWithSearch,
    ...params,
  });

  gtagEvent("page_view", {
    page_path: pathWithSearch,
    page_location: window.location.href,
    ...params,
  });

  if (meta.page_kind === "project" && meta.project_id) {
    gtagEvent("view_item", {
      item_id: meta.project_id,
      item_name: meta.project_title,
      item_category: meta.project_category,
      content_type: "portfolio_project",
      ...baseParams(),
    });
    gtagEvent("project_view", {
      project_id: meta.project_id,
      project_title: meta.project_title,
      project_category: meta.project_category,
      ...baseParams(),
    });
  }
}

export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  gtagEvent(eventName, baseParams(params));
}

/** Primary conversion — contact form submitted successfully. */
export function trackContactLead(input: {
  hasSubject: boolean;
  emailDomain?: string;
}): void {
  trackEvent("generate_lead", {
    lead_type: "contact",
    conversion_goal: "contact",
    has_subject: input.hasSubject,
    ...(input.emailDomain ? { email_domain: input.emailDomain } : {}),
  });
  trackEvent("contact_submit_success", {
    has_subject: input.hasSubject,
    ...(input.emailDomain ? { email_domain: input.emailDomain } : {}),
  });
}

export function trackContactSubmitError(message: string): void {
  trackEvent("contact_submit_error", { error_message: message.slice(0, 120) });
}

export function trackWizardStart(input: {
  entrySource: string;
  offeringKind?: string | null;
  offeringId?: string | null;
  productId?: string | null;
  initialStep: number;
}): void {
  trackEvent("wizard_start", {
    entry_source: input.entrySource,
    offering_kind: input.offeringKind ?? "(none)",
    offering_id: input.offeringId ?? "(none)",
    product_id: input.productId ?? "(none)",
    initial_step: input.initialStep,
  });
}

export function trackWizardStepView(input: {
  step: number;
  productId?: string | null;
  moduleCount?: number;
}): void {
  const stepMeta = CONFIGURATOR_STEPS[input.step - 1];
  trackEvent("wizard_step_view", {
    step_number: input.step,
    step_label: stepMeta?.label ?? `Step ${input.step}`,
    product_id: input.productId ?? "(none)",
    ...(input.moduleCount != null ? { module_count: input.moduleCount } : {}),
  });
}

export function trackWizardStepComplete(input: {
  fromStep: number;
  toStep: number;
  productId?: string | null;
}): void {
  const fromMeta = CONFIGURATOR_STEPS[input.fromStep - 1];
  trackEvent("wizard_step_complete", {
    from_step: input.fromStep,
    from_step_label: fromMeta?.label ?? `Step ${input.fromStep}`,
    to_step: input.toStep,
    product_id: input.productId ?? "(none)",
  });
}

/** Primary conversion — project wizard brief submitted successfully. */
export function trackWizardLead(input: {
  productId?: string | null;
  offeringKind?: string | null;
  offeringId?: string | null;
  moduleCount: number;
  timeline?: string;
  budget?: string;
  emailDomain?: string;
  confirmationSent?: boolean;
}): void {
  const payload = {
    lead_type: "project_brief",
    conversion_goal: "contact",
    product_id: input.productId ?? "(none)",
    offering_kind: input.offeringKind ?? "(none)",
    offering_id: input.offeringId ?? "(none)",
    module_count: input.moduleCount,
    timeline: input.timeline ?? "(none)",
    budget: input.budget ?? "(none)",
    wizard_max_step: CONFIGURATOR_STEPS.length,
    ...(input.emailDomain ? { email_domain: input.emailDomain } : {}),
    ...(input.confirmationSent != null ? { confirmation_sent: input.confirmationSent } : {}),
  };

  trackEvent("generate_lead", payload);
  trackEvent("wizard_submit_success", payload);
}

export function trackWizardSubmitError(message: string, step: number): void {
  trackEvent("wizard_submit_error", {
    error_message: message.slice(0, 120),
    step_number: step,
  });
}

export function trackWizardSubmitAttempt(input: {
  step: number;
  productId?: string | null;
}): void {
  trackEvent("wizard_submit_attempt", {
    step_number: input.step,
    product_id: input.productId ?? "(none)",
  });
}
